import { useEffect, useMemo, useRef, useState } from 'react';
import type { PaletteEntry } from '../../model/format.ts';
import { customSheet, findCustomTile, saveCustomSheet, setSheetColors, setSheetSwap, setTileShape, spritePixels, tileImg } from '../../data/customTiles.ts';
import { styleOverride } from '../../terrain/index.ts';
import { CaseDiagram, outlineSegments } from '../../terrain/CasesTab.ts';
import { ChoiceThumb, drawSpriteAt, pairs, spriteTable } from '../../terrain/ttShared.ts';
import { shapePoly } from '../../terrain/shapes.ts';
import { NB } from '../../terrain/keys.ts';
import { useEditor, type TileEditRequest } from '../store.ts';
import { CORNER_LABEL, cornerGuide } from '../spikes.ts';
import { Icon } from '../widgets.tsx';
import {
  CLEAR,
  applyFlags,
  applySwap,
  clearMask,
  cloneImg,
  decodePx,
  encodePx,
  cssOf,
  extract,
  fillPoints,
  flipH,
  flipV,
  getPx,
  hexOf,
  lassoMask,
  linePoints,
  nearestColor,
  paletteOf,
  parseHex,
  pixelPerfectDrops,
  rectMask,
  rectPoints,
  rotate,
  sameImg,
  setPx,
  shift,
  stamp,
  type Floating,
  type Img,
  type Mask,
} from './img.ts';
import { applyLink, beginSession, cancelSession, colorKeyOf, contextTiles, removeColor, saveSession, seamProblems, sessionImages, swapMap, tilesetPalette, writeColor, type EditSession, type LinkMode } from './session.ts';
import { SourcePane, type SourcePick } from './SourcePane.tsx';
import { RefColumn, loadRefs, sameRef, storeRefs, type RefLayout, type RefTile } from './RefPane.tsx';
import { PaneResizer } from './PaneResizer.tsx';
import { useSettings } from '../settings.ts';
import './tileEditor.css';

const N = 16;
type Tool = 'pencil' | 'eraser' | 'line' | 'rect' | 'fill' | 'picker' | 'select' | 'lasso';
const TOOLS: { id: Tool; icon: string; label: string; key: string; tip?: string }[] = [
  { id: 'pencil', icon: 'pencil', label: 'Pencil', key: 'b', tip: 'Shift-click: a line from the last point. On a tile beside the canvas: paints its pixels into your tile' },
  { id: 'eraser', icon: 'erase', label: 'Eraser', key: 'e', tip: 'Shift-click: erase a line from the last point' },
  { id: 'line', icon: 'line', label: 'Line', key: 'l' },
  { id: 'rect', icon: 'rect', label: 'Rectangle', key: 'u' },
  { id: 'fill', icon: 'bucket', label: 'Fill', key: 'g' },
  { id: 'picker', icon: 'eyedropper', label: 'Pick colour', key: 'i' },
  { id: 'select', icon: 'marquee', label: 'Select', key: 'm' },
  { id: 'lasso', icon: 'lasso', label: 'Lasso', key: 'a' },
];

/** pasted or copied pixels, kept between editor sessions */
let clipboard: Img | null = null;
const LONG_PRESS_MS = 450;

type Gesture =
  | { kind: 'none' }
  | { kind: 'draw'; base: Img; pts: [number, number][]; color: number; id: number }
  | { kind: 'shape'; base: Img; x0: number; y0: number; x1: number; y1: number; color: number; id: number }
  | { kind: 'select'; x0: number; y0: number; x1: number; y1: number; id: number }
  | { kind: 'lasso'; pts: [number, number][]; id: number }
  | { kind: 'float'; sx: number; sy: number; fx: number; fy: number; id: number }
  | { kind: 'pan'; sx: number; sy: number; px: number; py: number; id: number }
  | { kind: 'pinch'; d0: number; z0: number; cx: number; cy: number; px: number; py: number };

interface Opts {
  pixelPerfect: boolean;
  mirrorX: boolean;
  mirrorY: boolean;
  contiguous: boolean;
  filled: boolean;
  dither: boolean;
}

interface Show {
  neighbours: boolean;
  other: boolean;
  guide: boolean;
  grid: boolean;
  seams: boolean;
  collision: boolean;
  /** darken the neighbours so the edited cell stands out */
  dim: boolean;
}

const loadJson = <T,>(k: string, d: T): T => {
  try {
    const v = localStorage.getItem(k);
    return v ? { ...d, ...JSON.parse(v) } : d;
  } catch {
    return d;
  }
};

export function TileEditorHost() {
  const req = useEditor((s) => s.tileEdit);
  const doc = useEditor((s) => !!s.doc);
  const picking = useEditor((s) => !!s.pickTile);
  if (!req || !doc) return null;
  return <TileEditor key={JSON.stringify(req)} req={req} hidden={picking} />;
}

function TileEditor({ req, hidden }: { req: TileEditRequest; hidden: boolean }) {
  // starting a session creates and links the tile, which notifies other components: not during render
  const [sess, setSess] = useState<EditSession | string | null>(null);
  useEffect(() => setSess(beginSession(useEditor.getState().doc!, req, req.start ? decodePx(req.start, N) : null)), [req]);
  if (!sess) return null;
  if (typeof sess === 'string') return <EditorError message={sess} />;
  return <EditorBody sess={sess} hidden={hidden} />;
}

function EditorError({ message }: { message: string }) {
  return (
    <div className="te-back">
      <div className="te-error">
        <p>{message}</p>
        <button className="btn" onClick={() => useEditor.getState().set({ tileEdit: null })}>Close</button>
      </div>
    </div>
  );
}

function EditorBody({ sess, hidden }: { sess: EditSession; hidden: boolean }) {
  const doc = useEditor((s) => s.doc)!;
  const styleId = sess.styleId;
  const [imgs, setImgs] = useState<Record<string, Img>>(() => sessionImages(sess));
  const [color, setColor] = useState(sess.cellColor in imgs ? sess.cellColor : '');
  const img = imgs[color] ?? imgs[''];
  const [tool, setTool] = useState<Tool>('pencil');
  const [opts, setOpts] = useState<Opts>(() => loadJson('ple:te-opts', { pixelPerfect: true, mirrorX: false, mirrorY: false, contiguous: true, filled: false, dither: false }));
  const [show, setShow] = useState<Show>(() => loadJson('ple:te-show', { neighbours: true, other: true, guide: true, grid: true, seams: true, collision: false, dim: true }));
  const [primary, setPrimary] = useState<number>(() => firstColor(img) ?? 0x000000ff);
  const [secondary, setSecondary] = useState<number>(CLEAR);
  const [mask, setMask] = useState<Mask | null>(null);
  const [floating, setFloating] = useState<Floating | null>(null);
  const [undo, setUndo] = useState<{ color: string; img: Img }[]>([]);
  const [redo, setRedo] = useState<{ color: string; img: Img }[]>([]);
  const [view, setView] = useState({ z: 0, px: 0, py: 0 });
  const [palTab, setPalTab] = useState<'tileset' | 'tile' | 'saved'>('tileset');
  const [lock, setLock] = useState(() => localStorage.getItem('ple:te-lock') === '1');
  const [sync, setSync] = useState(true);
  const [source, setSource] = useState<SourcePick | null>(null);
  const [refs, setRefs] = useState<RefTile[]>(() => loadRefs(sess.styleId));
  const [refLayout, setRefLayout] = useState<RefLayout>(() => (localStorage.getItem('ple:te-reflayout') === 'grid' ? 'grid' : 'fit'));
  const settings = useSettings();
  const [refsW, setRefsW] = useState(settings.teRefsWidth);
  const [sideW, setSideW] = useState(settings.teSideWidth);
  const [refHover, setRefHover] = useState<[number, number] | null>(null);
  const [tbHover, setTbHover] = useState(false);
  const tbTimer = useRef(0);
  // as the level editor's tool bar: touch has no hover, and a short delay stops it flashing open
  const tbHoverTo = (on: boolean, e: React.PointerEvent) => {
    if (e.pointerType === 'touch') return;
    clearTimeout(tbTimer.current);
    tbTimer.current = window.setTimeout(() => setTbHover(on), on ? 120 : 200);
  };
  useEffect(() => () => clearTimeout(tbTimer.current), []);
  const [linkMode, setLinkMode] = useState<LinkMode>(sess.req.link ?? 'replace');
  const [status, setStatus] = useState('');
  const [hover, setHover] = useState<[number, number] | null>(null);
  const [saved, setSaved] = useState<string[]>(() => customSheet(styleId)?.colors ?? []);
  const [swapEdit, setSwapEdit] = useState<string | null>(null);
  const [, setTick] = useState(0);
  const tile = findCustomTile(styleId, sess.tileN);
  const [collision, setCollision] = useState<'shape' | 'pixels'>(tile?.shape !== undefined ? 'shape' : 'pixels');
  const gesture = useRef<Gesture>({ kind: 'none' });
  /** where the last pencil / eraser stroke ended: shift-click draws a line from it */
  const lastPt = useRef<[number, number] | null>(null);
  const [shiftHeld, setShiftHeld] = useState(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const longPress = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<[number, number]>([400, 400]);
  const done = useRef(false);

  useEffect(() => localStorage.setItem('ple:te-opts', JSON.stringify(opts)), [opts]);
  useEffect(() => localStorage.setItem('ple:te-show', JSON.stringify(show)), [show]);
  useEffect(() => localStorage.setItem('ple:te-lock', lock ? '1' : '0'), [lock]);
  useEffect(() => storeRefs(styleId, refs), [styleId, refs]);
  useEffect(() => localStorage.setItem('ple:te-reflayout', refLayout), [refLayout]);
  useEffect(() => {
    if (sess.created) applyLink(sess, linkMode);
  }, [linkMode, sess]);
  // closed any other way than Save / Cancel (another tile opened, level closed): drop the unsaved changes
  useEffect(
    () => () => {
      if (done.current) return;
      done.current = true;
      cancelSession(sess);
    },
    [sess],
  );

  // ---------- context around the cell ----------
  const context = useMemo(() => contextTiles(doc, sess), [doc, sess]);
  const seams = useMemo(() => (show.seams ? seamProblems(img, context.edit) : []), [img, context, show.seams]);
  const tsPalette = useMemo(() => tilesetPalette(styleId, color), [styleId, color]);
  const savedColors = useMemo(() => saved.map(parseHex).filter(Boolean), [saved]);
  const lockSet = useMemo(() => new Set([...tsPalette, ...savedColors]), [tsPalette, savedColors]);
  const tilePalette = useMemo(() => paletteOf([img, ...(source ? [sourceImg(source)].filter((x): x is Img => !!x) : [])], 64), [img, source]);
  const offPalette = useMemo(() => {
    if (!lock) return [] as [number, number][];
    const out: [number, number][] = [];
    for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
      const c = getPx(img, x, y);
      if (c && !lockSet.has(c)) out.push([x, y]);
    }
    return out;
  }, [img, lock, lockSet]);

  // ---------- applying changes ----------
  const writeAll = (next: Record<string, Img>, changed: string) => {
    writeColor(sess, changed, next[changed]);
    if (sync)
      for (const k of Object.keys(next)) {
        if (k === changed) continue;
        next[k] = applySwap(next[changed], swapMap(styleId, changed, k));
        writeColor(sess, k, next[k]);
      }
  };
  /** live: no undo step (strokes in progress) */
  const put = (next: Img, live = false, base?: Img) => {
    if (!live) {
      const before = base ?? img;
      if (!base && sameImg(before, next)) return;
      if (!sameImg(before, next)) {
        setUndo((u) => [...u.slice(-199), { color, img: before }]);
        setRedo([]);
      }
    }
    const all = { ...imgs, [color]: next };
    writeAll(all, color);
    setImgs(all);
  };
  const doUndo = () => {
    if (floating) return setFloating(null);
    const e = undo[undo.length - 1];
    if (!e) return;
    setUndo(undo.slice(0, -1));
    setRedo([...redo, { color: e.color, img: imgs[e.color] }]);
    const all = { ...imgs, [e.color]: e.img };
    writeAll(all, e.color);
    setImgs(all);
    setColor(e.color);
  };
  const doRedo = () => {
    const e = redo[redo.length - 1];
    if (!e) return;
    setRedo(redo.slice(0, -1));
    setUndo([...undo, { color: e.color, img: imgs[e.color] }]);
    const all = { ...imgs, [e.color]: e.img };
    writeAll(all, e.color);
    setImgs(all);
    setColor(e.color);
  };

  const expand = (pts: [number, number][]) => {
    const out: [number, number][] = [];
    for (const [x, y] of pts) {
      out.push([x, y]);
      if (opts.mirrorX) out.push([N - 1 - x, y]);
      if (opts.mirrorY) out.push([x, N - 1 - y]);
      if (opts.mirrorX && opts.mirrorY) out.push([N - 1 - x, N - 1 - y]);
    }
    return out;
  };
  const maskRef = useRef(mask);
  maskRef.current = mask;
  const allowed = (x: number, y: number) => {
    const m = maskRef.current;
    return x >= 0 && y >= 0 && x < N && y < N && (!m || m[y * N + x] === 1);
  };
  const deselect = () => {
    if (floating) commitFloating();
    maskRef.current = null;
    setMask(null);
  };
  const paint = (base: Img, pts: [number, number][], c: number | ((x: number, y: number) => number)) => {
    const o = cloneImg(base);
    for (const [x, y] of expand(pts)) if (allowed(x, y)) setPx(o, x, y, typeof c === 'number' ? c : c(x, y));
    return o;
  };
  const ditherColor = (c: number) => (opts.dither ? (x: number, y: number) => ((x + y) & 1 ? secondary : c) : c);
  const commitFloating = () => {
    if (!floating) return;
    put(stamp(img, floating));
    setFloating(null);
    setMask(null);
  };
  const liftSelection = (): Floating | null => {
    if (floating) return floating;
    if (!mask) return null;
    const f = extract(img, mask);
    if (!f) return null;
    put(clearMask(img, mask));
    setFloating(f);
    setMask(null);
    return f;
  };
  const transform = (fn: (a: Img) => Img) => {
    if (floating) return setFloating({ ...floating, img: fn(floating.img) });
    if (mask) {
      const f = extract(img, mask);
      if (!f) return;
      put(clearMask(img, mask));
      const t = fn(f.img);
      setFloating({ img: t, x: f.x, y: f.y });
      setMask(null);
      return;
    }
    put(fn(img));
  };
  const copy = (cut = false) => {
    const f = floating ?? (mask ? extract(img, mask) : { img: cloneImg(img), x: 0, y: 0 });
    if (!f) return;
    clipboard = cloneImg(f.img);
    setStatus(`Copied ${f.img.w}×${f.img.h}`);
    if (cut) {
      if (floating) setFloating(null);
      else if (mask) {
        put(clearMask(img, mask));
        setMask(null);
      } else put(cloneImg({ ...img, d: new Uint8ClampedArray(img.d.length) }));
    }
  };
  const pasteImg = (p: Img, at?: [number, number]) => {
    commitFloating();
    const x = at ? at[0] : Math.floor((N - p.w) / 2), y = at ? at[1] : Math.floor((N - p.h) / 2);
    setFloating({ img: cloneImg(p), x, y });
    setMask(null);
    setTool('select');
  };
  const del = () => {
    if (floating) return setFloating(null);
    if (mask) {
      put(clearMask(img, mask));
      setMask(null);
    }
  };
  const nudge = (dx: number, dy: number) => {
    if (floating) return setFloating({ ...floating, x: floating.x + dx, y: floating.y + dy });
    if (mask) {
      const f = liftSelection();
      if (f) setFloating({ ...f, x: f.x + dx, y: f.y + dy });
      return;
    }
    put(shift(img, dx, dy));
  };
  const snapToPalette = () => {
    const pal = [...lockSet];
    if (!pal.length) return;
    const o = cloneImg(img);
    for (const [x, y] of offPalette) setPx(o, x, y, nearestColor(getPx(img, x, y), pal));
    put(o);
  };
  const pickColor = (c: number, secondaryBtn = false) => {
    if (lock && c && !lockSet.has(c)) c = nearestColor(c, [...lockSet]);
    if (secondaryBtn) setSecondary(c);
    else setPrimary(c);
  };

  // ---------- colour versions ----------
  const switchColor = (k: string) => {
    commitFloating();
    if (!imgs[k]) {
      const made = applySwap(img, swapMap(styleId, color, k));
      const all = { ...imgs, [k]: made };
      writeColor(sess, k, made);
      setImgs(all);
    }
    setColor(k);
  };
  const makeAllColors = () => {
    const all = { ...imgs };
    for (let vi = 0; vi < sess.variants.length; vi++) {
      const k = colorKeyOf(sess, vi);
      if (all[k]) continue;
      all[k] = applySwap(img, swapMap(styleId, color, k));
      writeColor(sess, k, all[k]);
    }
    setImgs(all);
  };
  const dropColor = (k: string) => {
    if (!k) return;
    const all = { ...imgs };
    delete all[k];
    removeColor(sess, k);
    setImgs(all);
    if (color === k) setColor('');
  };

  // ---------- save / cancel ----------
  const close = () => useEditor.getState().set({ tileEdit: null, pickTile: null });
  const cancel = () => {
    if (done.current) return;
    done.current = true;
    cancelSession(sess);
    close();
  };
  const save = async (next?: TileEditRequest | null) => {
    if (done.current) return;
    commitFloating();
    done.current = true;
    setStatus('saving…');
    try {
      await saveSession(sess);
      useEditor.getState().notify('Tile saved');
    } catch {
      useEditor.getState().notify('Tile saved on this device');
    }
    useEditor.getState().set(next ? { tileEdit: next, pickTile: null, fixCell: { container: next.container, x: next.x, y: next.y } } : { tileEdit: null, pickTile: null });
  };
  const saveNext = () => {
    const q = sess.req.queue ?? [];
    const [n, ...rest] = q;
    save(n ? { ...n, scope: 'exact', mode: 'new', queue: rest } : null);
  };
  const saveAnother = () => save({ ...sess.req, mode: 'new', link: 'add', start: encodePx(img), queue: sess.req.queue });

  // ---------- view ----------
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize([el.clientWidth, el.clientHeight]));
    ro.observe(el);
    setSize([el.clientWidth, el.clientHeight]);
    return () => ro.disconnect();
  }, []);
  const span = show.neighbours ? 3 * N + 4 : N + 4;
  const fitZ = Math.max(4, Math.floor(Math.min(size[0], size[1]) / span));
  const z = view.z || fitZ;
  const ox = Math.round(size[0] / 2 - (N / 2) * z + view.px), oy = Math.round(size[1] / 2 - (N / 2) * z + view.py);
  const toPx = (cx: number, cy: number): [number, number] => {
    const r = canvasRef.current!.getBoundingClientRect();
    return [Math.floor((cx - r.left - ox) / z), Math.floor((cy - r.top - oy) / z)];
  };
  const toPxF = (cx: number, cy: number): [number, number] => {
    const r = canvasRef.current!.getBoundingClientRect();
    return [(cx - r.left - ox) / z, (cy - r.top - oy) / z];
  };

  // composite of the 3×3 cells at texture resolution
  const composite = useMemo(() => {
    const c = document.createElement('canvas');
    c.width = c.height = 3 * N;
    return c;
  }, []);
  const redraw = () => setTick((t) => t + 1);
  const drawComposite = () => {
    const g = composite.getContext('2d')!;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, 3 * N, 3 * N);
    g.imageSmoothingEnabled = false;
    const tileAt = (t: { dx: number; dy: number; pe: PaletteEntry; f: number }) => {
      g.setTransform(N, 0, 0, -N, (t.dx + 1) * N + N / 2, (1 - t.dy) * N + N / 2);
      drawSpriteAt(g, t.pe, t.f, redraw);
    };
    const centre = (t: { dx: number; dy: number }) => t.dx === 0 && t.dy === 0;
    // the other terrain layer (grass over dirt, dirt under grass) shows or hides in every cell
    const otherBelow = sess.req.layer === 'grass' || sess.req.layer === 'spike';
    const shown = (t: { dx: number; dy: number }, isOther: boolean) => (!isOther || show.other) && (centre(t) || show.neighbours);
    for (const t of context.below) if (shown(t, otherBelow)) tileAt(t);
    if (show.neighbours) for (const t of context.edit) if (!centre(t)) tileAt(t);
    g.setTransform(1, 0, 0, 1, 0, 0);
    const cur = floating ? stamp(img, floating) : img;
    const tmp = document.createElement('canvas');
    tmp.width = tmp.height = N;
    tmp.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(cur.d), N, N), 0, 0);
    g.drawImage(tmp, N, N);
    for (const t of context.above) if (shown(t, !otherBelow)) tileAt(t);
    g.setTransform(1, 0, 0, 1, 0, 0);
  };

  /** 1:1 and 2× of the cell with its neighbours */
  const drawPreview = () => {
    const c = previewRef.current;
    if (!c) return;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.clearRect(0, 0, c.width, c.height);
    if (show.neighbours) {
      g.drawImage(composite, 0, 0);
      g.drawImage(composite, 3 * N + 6, 0, 6 * N, 6 * N);
    } else {
      g.drawImage(composite, N, N, N, N, 0, 0, N, N);
      g.drawImage(composite, N, N, N, N, 3 * N + 6, 0, 2 * N, 2 * N);
    }
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(size[0] * dpr);
    c.height = Math.round(size[1] * dpr);
    const g = c.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.imageSmoothingEnabled = false;
    g.fillStyle = '#121218';
    g.fillRect(0, 0, size[0], size[1]);
    drawComposite();
    drawPreview();
    const T = N * z;
    // checkerboard under the edited tile
    for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
      g.fillStyle = (x + y) & 1 ? '#2b2b36' : '#33333f';
      g.fillRect(ox + x * z, oy + y * z, z, z);
    }
    if (show.neighbours) {
      g.drawImage(composite, ox - T, oy - T, 3 * T, 3 * T);
      if (show.dim) {
        g.fillStyle = 'rgba(10,10,16,0.35)';
        g.beginPath();
        g.rect(ox - T, oy - T, 3 * T, 3 * T);
        g.rect(ox, oy, T, T);
        g.fill('evenodd');
      }
    } else g.drawImage(composite, N, N, N, N, ox, oy, T, T);
    // guides: the cell's solid shape and the outline facing the air
    const cell = (u: number, v: number): [number, number] => [ox + u * T, oy + (1 - v) * T];
    if (show.guide) {
      g.save();
      g.lineWidth = 1;
      g.strokeStyle = 'rgba(255,255,255,0.18)';
      for (let i = 0; i < 8; i++) {
        const p = shapePoly(sess.info.nb[i]);
        if (!p.length) continue;
        const [dx, dy] = NB[i];
        g.beginPath();
        p.forEach(([u, v], k) => (k ? g.lineTo(...cell(u + dx, v + dy)) : g.moveTo(...cell(u + dx, v + dy))));
        g.closePath();
        g.stroke();
      }
      const { lines, dots } = sess.req.layer === 'spike' && sess.req.corner
        ? { lines: cornerGuide(sess.req.corner).lines, dots: [] as [number, number][] }
        : outlineSegments(sess.info[sess.req.layer === 'grass' ? 'grass' : 'dirt'].key || sess.key, sess.info.own);
      g.strokeStyle = g.fillStyle = sess.req.layer === 'grass' ? '#86e04f' : sess.req.layer === 'spike' ? '#ff8a5c' : '#5fd0ff';
      g.lineWidth = Math.max(2, z / 4);
      g.lineCap = 'round';
      g.globalAlpha = 0.85;
      for (const [x, y, x2, y2] of lines) {
        g.beginPath();
        g.moveTo(...cell(x, y));
        g.lineTo(...cell(x2, y2));
        g.stroke();
      }
      for (const [x, y] of dots) {
        g.beginPath();
        g.arc(...cell(x, y), Math.max(3, z / 3), 0, Math.PI * 2);
        g.fill();
      }
      g.restore();
    }
    if (show.collision) {
      const p = shapePoly(sess.info.own);
      g.save();
      g.beginPath();
      g.rect(ox, oy, T, T);
      p.forEach(([u, v], k) => (k ? g.lineTo(...cell(u, v)) : g.moveTo(...cell(u, v))));
      g.closePath();
      g.fillStyle = 'rgba(255,90,90,0.22)';
      g.fill('evenodd');
      g.restore();
    }
    if (show.grid && z >= 6) {
      g.strokeStyle = 'rgba(255,255,255,0.07)';
      g.lineWidth = 1;
      g.beginPath();
      for (let i = 1; i < N; i++) {
        g.moveTo(ox + i * z + 0.5, oy);
        g.lineTo(ox + i * z + 0.5, oy + T);
        g.moveTo(ox, oy + i * z + 0.5);
        g.lineTo(ox + T, oy + i * z + 0.5);
      }
      g.stroke();
    }
    g.strokeStyle = '#e0b25a';
    g.lineWidth = 1;
    g.strokeRect(ox - 0.5, oy - 0.5, T + 1, T + 1);
    if (opts.mirrorX || opts.mirrorY) {
      g.save();
      g.setLineDash([4, 4]);
      g.strokeStyle = 'rgba(224,178,90,0.7)';
      g.beginPath();
      if (opts.mirrorX) {
        g.moveTo(ox + T / 2, oy - 6);
        g.lineTo(ox + T / 2, oy + T + 6);
      }
      if (opts.mirrorY) {
        g.moveTo(ox - 6, oy + T / 2);
        g.lineTo(ox + T + 6, oy + T / 2);
      }
      g.stroke();
      g.restore();
    }
    // seams: edge pixels whose opacity breaks against the neighbour
    if (seams.length) {
      g.fillStyle = '#ff5d5d';
      const k = Math.max(3, z / 3);
      for (const s of seams) {
        if (s.side === 'L') g.fillRect(ox - k - 2, oy + s.i * z, k, z);
        if (s.side === 'R') g.fillRect(ox + T + 2, oy + s.i * z, k, z);
        if (s.side === 'T') g.fillRect(ox + s.i * z, oy - k - 2, z, k);
        if (s.side === 'B') g.fillRect(ox + s.i * z, oy + T + 2, z, k);
      }
    }
    if (offPalette.length) {
      g.fillStyle = '#ff3b8d';
      const k = Math.max(2, Math.round(z / 4));
      for (const [x, y] of offPalette) g.fillRect(ox + x * z + z - k, oy + y * z, k, k);
    }
    // selection, floating piece, gesture previews
    const ants = (m: Mask) => {
      g.save();
      g.strokeStyle = '#fff';
      g.setLineDash([3, 3]);
      g.beginPath();
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        if (!m[y * N + x]) continue;
        const X = ox + x * z, Y = oy + y * z;
        if (x === 0 || !m[y * N + x - 1]) { g.moveTo(X + 0.5, Y); g.lineTo(X + 0.5, Y + z); }
        if (x === N - 1 || !m[y * N + x + 1]) { g.moveTo(X + z - 0.5, Y); g.lineTo(X + z - 0.5, Y + z); }
        if (y === 0 || !m[(y - 1) * N + x]) { g.moveTo(X, Y + 0.5); g.lineTo(X + z, Y + 0.5); }
        if (y === N - 1 || !m[(y + 1) * N + x]) { g.moveTo(X, Y + z - 0.5); g.lineTo(X + z, Y + z - 0.5); }
      }
      g.stroke();
      g.restore();
    };
    if (mask) ants(mask);
    if (floating) {
      // parts outside the tile are drawn too, so a piece can be dragged in from the side
      const t = document.createElement('canvas');
      t.width = floating.img.w;
      t.height = floating.img.h;
      t.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(floating.img.d), floating.img.w, floating.img.h), 0, 0);
      g.globalAlpha = 0.55;
      g.drawImage(t, ox + floating.x * z, oy + floating.y * z, floating.img.w * z, floating.img.h * z);
      g.globalAlpha = 1;
      g.save();
      g.setLineDash([5, 3]);
      g.strokeStyle = '#4fc3ff';
      g.strokeRect(ox + floating.x * z + 0.5, oy + floating.y * z + 0.5, floating.img.w * z - 1, floating.img.h * z - 1);
      g.restore();
    }
    const ge = gesture.current;
    if (ge.kind === 'select') ants(rectMask(N, N, ge.x0, ge.y0, ge.x1, ge.y1));
    if (ge.kind === 'lasso' && ge.pts.length > 1) {
      g.save();
      g.strokeStyle = '#fff';
      g.setLineDash([3, 3]);
      g.beginPath();
      ge.pts.forEach(([x, y], i) => (i ? g.lineTo(ox + x * z, oy + y * z) : g.moveTo(ox + x * z, oy + y * z)));
      g.stroke();
      g.restore();
    }
    if (refHover) {
      g.strokeStyle = '#4fc3ff';
      g.lineWidth = 2;
      g.strokeRect(ox + refHover[0] * z + 1, oy + refHover[1] * z + 1, z - 2, z - 2);
    }
    const lp = lastPt.current;
    if (shiftHeld && lp && hover && ge.kind === 'none' && (tool === 'pencil' || tool === 'eraser')) {
      // the line a shift-click draws from the end of the last stroke
      g.fillStyle = tool === 'eraser' ? 'rgba(255,90,90,0.35)' : 'rgba(255,255,255,0.3)';
      g.strokeStyle = tool === 'eraser' ? 'rgba(255,90,90,0.9)' : 'rgba(79,195,255,0.9)';
      g.lineWidth = 1;
      for (const [px, py] of expand(linePoints(lp[0], lp[1], hover[0], hover[1])))
        if (allowed(px, py)) {
          g.fillRect(ox + px * z, oy + py * z, z, z);
          g.strokeRect(ox + px * z + 0.5, oy + py * z + 0.5, z - 1, z - 1);
        }
    }
    if (hover && ge.kind === 'none' && hover[0] >= -N && hover[1] >= -N && hover[0] < 2 * N && hover[1] < 2 * N) {
      g.strokeStyle = 'rgba(255,255,255,0.8)';
      g.lineWidth = 1;
      g.strokeRect(ox + hover[0] * z + 0.5, oy + hover[1] * z + 0.5, z - 1, z - 1);
    }
  });

  // ---------- pointer input ----------
  const cancelLongPress = () => {
    if (longPress.current) clearTimeout(longPress.current);
    longPress.current = undefined;
  };
  const compositeColor = (x: number, y: number): number => {
    if (x >= 0 && y >= 0 && x < N && y < N) {
      const c = getPx(floating ? stamp(img, floating) : img, x, y);
      if (c || !show.neighbours) return c;
    }
    if (!show.neighbours && (x < 0 || y < 0 || x >= N || y >= N)) return CLEAR;
    const d = composite.getContext('2d')!.getImageData(x + N, y + N, 1, 1).data;
    return d[3] ? ((d[0] << 24) | (d[1] << 16) | (d[2] << 8) | d[3]) >>> 0 : CLEAR;
  };
  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g0 = gesture.current;
    if (pointers.current.size === 2) {
      // second finger: undo the stroke it interrupted and pinch the view
      cancelLongPress();
      if (g0.kind === 'draw' || g0.kind === 'shape') put(g0.base, true);
      const [a, b] = [...pointers.current.values()];
      gesture.current = { kind: 'pinch', d0: Math.hypot(a.x - b.x, a.y - b.y), z0: z, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, px: view.px, py: view.py };
      return;
    }
    if (pointers.current.size > 2) return;
    const [x, y] = toPx(e.clientX, e.clientY);
    const id = e.pointerId;
    if (e.button === 1 || (e.button === 0 && spaceDown.current)) {
      gesture.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, px: view.px, py: view.py, id };
      return;
    }
    const alt = e.altKey || tool === 'picker';
    if (alt) {
      pickColor(compositeColor(x, y), e.button === 2);
      return;
    }
    if (e.pointerType === 'touch')
      longPress.current = setTimeout(() => {
        const g = gesture.current;
        if (g.kind === 'draw' || g.kind === 'shape') put(g.base, true);
        gesture.current = { kind: 'none' };
        pickColor(compositeColor(x, y));
        setStatus('Picked a colour');
      }, LONG_PRESS_MS);
    // drawing that starts outside the selection drops the selection instead of doing nothing
    if (mask && tool !== 'select' && tool !== 'lasso' && !(x >= 0 && y >= 0 && x < N && y < N && mask[y * N + x])) {
      maskRef.current = null;
      setMask(null);
      setStatus('Selection cleared');
    }
    const drawColor = tool === 'eraser' ? CLEAR : e.button === 2 ? secondary : primary;
    // drawing applies a floating piece first
    const base = floating && tool !== 'select' && tool !== 'lasso' ? stamp(img, floating) : img;
    if (base !== img) commitFloating();
    switch (tool) {
      case 'pencil':
      case 'eraser': {
        const from = e.shiftKey ? lastPt.current : null;
        const pts = from ? linePoints(from[0], from[1], x, y) : [[x, y] as [number, number]];
        gesture.current = { kind: 'draw', base, pts, color: drawColor, id };
        put(paint(base, pts, drawColor), true);
        return;
      }
      case 'line':
      case 'rect':
        gesture.current = { kind: 'shape', base, x0: x, y0: y, x1: x, y1: y, color: drawColor, id };
        put(paint(base, [[x, y]], drawColor), true);
        return;
      case 'fill': {
        if (!allowed(x, y)) return;
        const pts = fillPoints(base, x, y, opts.contiguous).filter(([px, py]) => allowed(px, py));
        put(paint(base, pts, e.button !== 2 ? ditherColor(primary) : drawColor), false, base);
        return;
      }
      case 'select':
      case 'lasso': {
        const fx = (e.clientX - canvasRef.current!.getBoundingClientRect().left - ox) / z, fy = (e.clientY - canvasRef.current!.getBoundingClientRect().top - oy) / z;
        if (floating && fx >= floating.x && fy >= floating.y && fx < floating.x + floating.img.w && fy < floating.y + floating.img.h) {
          gesture.current = { kind: 'float', sx: e.clientX, sy: e.clientY, fx: floating.x, fy: floating.y, id };
          return;
        }
        if (!floating && mask && allowed(x, y) && mask[y * N + x]) {
          const f = liftSelection();
          if (f) gesture.current = { kind: 'float', sx: e.clientX, sy: e.clientY, fx: f.x, fy: f.y, id };
          return;
        }
        commitFloating();
        setMask(null);
        gesture.current = tool === 'select' ? { kind: 'select', x0: x, y0: y, x1: x, y1: y, id } : { kind: 'lasso', pts: [toPxF(e.clientX, e.clientY)], id };
        redraw();
        return;
      }
    }
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const p = pointers.current.get(e.pointerId);
    if (p) {
      if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > 6) cancelLongPress();
      p.x = e.clientX;
      p.y = e.clientY;
    }
    const g = gesture.current;
    const [x, y] = toPx(e.clientX, e.clientY);
    if (!hover || hover[0] !== x || hover[1] !== y) setHover([x, y]);
    if (e.shiftKey !== shiftHeld) setShiftHeld(e.shiftKey);
    if (g.kind === 'pinch') {
      const [a, b] = [...pointers.current.values()];
      if (!a || !b) return;
      const nz = Math.max(2, Math.min(80, Math.round((g.z0 * Math.hypot(a.x - b.x, a.y - b.y)) / (g.d0 || 1))));
      setView({ z: nz, px: g.px + (a.x + b.x) / 2 - g.cx, py: g.py + (a.y + b.y) / 2 - g.cy });
      return;
    }
    if (!('id' in g) || g.id !== e.pointerId) return;
    switch (g.kind) {
      case 'pan':
        setView({ z, px: g.px + e.clientX - g.sx, py: g.py + e.clientY - g.sy });
        return;
      case 'draw': {
        const last = g.pts[g.pts.length - 1];
        if (last[0] === x && last[1] === y) return;
        for (const q of linePoints(last[0], last[1], x, y).slice(1)) g.pts.push(q);
        const drops = opts.pixelPerfect && g.color !== CLEAR ? pixelPerfectDrops(g.pts) : new Set<number>();
        put(paint(g.base, g.pts.filter((_, i) => !drops.has(i)), g.color), true);
        return;
      }
      case 'shape': {
        if (g.x1 === x && g.y1 === y) return;
        g.x1 = x;
        g.y1 = y;
        const pts = tool === 'line' ? linePoints(g.x0, g.y0, x, y) : rectPoints(g.x0, g.y0, x, y, opts.filled);
        put(paint(g.base, pts, opts.dither && tool === 'rect' && g.color !== CLEAR ? ditherColor(g.color) : g.color), true);
        return;
      }
      case 'select':
        g.x1 = x;
        g.y1 = y;
        redraw();
        return;
      case 'lasso':
        g.pts.push(toPxF(e.clientX, e.clientY));
        redraw();
        return;
      case 'float':
        if (floating) setFloating({ ...floating, x: g.fx + Math.round((e.clientX - g.sx) / z), y: g.fy + Math.round((e.clientY - g.sy) / z) });
        return;
    }
  };
  const onPointerUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    cancelLongPress();
    const g = gesture.current;
    if (g.kind === 'pinch') {
      if (pointers.current.size === 0) gesture.current = { kind: 'none' };
      return;
    }
    if (!('id' in g) || g.id !== e.pointerId) return;
    gesture.current = { kind: 'none' };
    if (e.type === 'pointercancel') {
      if (g.kind === 'draw' || g.kind === 'shape') put(g.base, true);
      return;
    }
    switch (g.kind) {
      case 'draw':
      case 'shape':
        lastPt.current = g.kind === 'draw' ? g.pts[g.pts.length - 1] : [g.x1, g.y1];
        // the stroke was shown live; record it as one undo step
        put(img, false, g.base);
        return;
      case 'select': {
        // a tap (no drag) deselects
        const m = g.x0 === g.x1 && g.y0 === g.y1 ? null : rectMask(N, N, g.x0, g.y0, g.x1, g.y1);
        setMask(m && m.some(Boolean) ? m : null);
        redraw();
        return;
      }
      case 'lasso': {
        const xs = g.pts.map((p) => p[0]), ys = g.pts.map((p) => p[1]);
        const m = Math.max(...xs) - Math.min(...xs) < 1 && Math.max(...ys) - Math.min(...ys) < 1 ? null : lassoMask(N, N, g.pts);
        setMask(m && m.some(Boolean) ? m : null);
        redraw();
        return;
      }
    }
  };
  const onWheel = (e: React.WheelEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    const f = e.deltaY < 0 ? 1.2 : 1 / 1.2;
    const nz = Math.max(2, Math.min(80, Math.round(z * f) === z ? z + Math.sign(f - 1) : Math.round(z * f)));
    // keep the point under the pointer still
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    const tx = (mx - ox) / z, ty = (my - oy) / z;
    const nox = mx - tx * nz, noy = my - ty * nz;
    setView({ z: nz, px: nox - (size[0] / 2 - (N / 2) * nz), py: noy - (size[1] / 2 - (N / 2) * nz) });
  };

  // ---------- keyboard ----------
  const spaceDown = useRef(false);
  const api = useRef({ doUndo, doRedo, copy, pasteImg, del, nudge, commitFloating, setTool, save, cancel, setMask, setFloating, floating, mask, swap: () => { setPrimary(secondary); setSecondary(primary); }, deselect });
  api.current = { doUndo, doRedo, copy, pasteImg, del, nudge, commitFloating, setTool, save, cancel, setMask, setFloating, floating, mask, swap: () => { setPrimary(secondary); setSecondary(primary); }, deselect };
  useEffect(() => {
    if (hidden) return;
    const down = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest?.('input, textarea, select')) return;
      const a = api.current;
      const k = e.key.toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (e.code === 'Space') spaceDown.current = true;
      if (e.key === 'Shift') setShiftHeld(true);
      if (mod && k === 'z') a[e.shiftKey ? 'doRedo' : 'doUndo']();
      else if (mod && k === 'y') a.doRedo();
      else if (mod && k === 'c') a.copy();
      else if (mod && k === 'x') a.copy(true);
      else if (mod && k === 'v') clipboard && a.pasteImg(clipboard);
      else if (mod && k === 's') a.save();
      else if (mod && k === 'a') a.setMask(rectMask(N, N, 0, 0, N - 1, N - 1));
      else if (mod && k === 'd') a.deselect();
      else if (k === 'delete' || k === 'backspace') a.del();
      else if (k === 'enter') a.commitFloating();
      else if (k === 'escape') {
        if (a.floating) a.setFloating(null);
        else if (a.mask) a.setMask(null);
        else return;
      } else if (k.startsWith('arrow')) a.nudge(k === 'arrowleft' ? -1 : k === 'arrowright' ? 1 : 0, k === 'arrowup' ? -1 : k === 'arrowdown' ? 1 : 0);
      else if (k === 'x' && !mod) a.swap();
      else if (!mod && TOOLS.some((t) => t.key === k)) a.setTool(TOOLS.find((t) => t.key === k)!.id);
      else return;
      e.preventDefault();
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceDown.current = false;
      if (e.key === 'Shift') setShiftHeld(false);
    };
    const blur = () => setShiftHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, [hidden]);

  // ---------- source tiles ----------
  const openRef = (r: RefTile) => setRefs((rs) => [...rs.filter((q) => !sameRef(q, r)), r].slice(-8));
  const pickFromLevel = () =>
    useEditor.getState().set({
      pickTile: (pe, flags) => {
        setSource({ pe, flags });
        openRef({ pe, flags });
        useEditor.getState().set({ pickTile: null });
      },
    });
  // the pencil on a tile beside the canvas paints that tile's pixels into the same spots of yours
  const cloneStroke = useRef<{ base: Img; pts: [number, number][] } | null>(null);
  const brushFromRef = (src: Img, pts: [number, number][], phase: 'start' | 'move' | 'end' | 'cancel') => {
    if (phase === 'start') {
      const base = floating ? stamp(img, floating) : img;
      if (base !== img) commitFloating();
      cloneStroke.current = { base, pts: [] };
    }
    const s = cloneStroke.current;
    if (!s) return;
    if (phase === 'cancel' || phase === 'end') {
      cloneStroke.current = null;
      if (phase === 'cancel') put(s.base, true);
      else {
        if (s.pts.length) lastPt.current = s.pts[s.pts.length - 1];
        put(img, false, s.base);
      }
      return;
    }
    s.pts.push(...pts);
    put(paint(s.base, s.pts, (x, y) => getPx(src, x, y)), true);
  };
  const pastePiece = (p: Img, x: number, y: number) => {
    clipboard = cloneImg(p);
    pasteImg(p, [x, y]);
    setStatus(p.w === N && p.h === N ? 'Pasted the whole tile: drag it, then Apply' : `Pasted ${p.w}×${p.h} at the same spot: drag it, then Apply`);
  };

  // ---------- render ----------
  const d = sess.d;
  const ov = styleOverride(styleId) ?? {};
  const table = spriteTable(d, ov);
  const terrainLayer = sess.req.layer === 'grass' ? 'grass' : 'dirt';
  const caseList = sess.req.layer === 'spike' ? null : ov.cases?.[terrainLayer]?.[sess.key] ?? null;
  const cs = sess.info[terrainLayer];
  const history = tile?.history ?? [];
  const tsName = ov.name ?? d.name;
  const queue = sess.req.queue ?? [];
  const setOpt = (p: Partial<Opts>) => setOpts({ ...opts, ...p });
  const setShowOpt = (p: Partial<Show>) => setShow({ ...show, ...p });
  const seamText = seams.length ? `${seams.length} edge pixel${seams.length > 1 ? 's' : ''} break against the neighbours` : '';
  const paletteShown = palTab === 'tileset' ? tsPalette : palTab === 'tile' ? tilePalette : savedColors;

  return (
    <div className="te-back" style={hidden ? { display: 'none' } : undefined}>
      <div className="te-root" onContextMenu={(e) => e.preventDefault()}>
        <div className="te-head">
          {sess.req.layer === 'spike' && sess.req.corner
            ? <CaseDiagram layer="dirt" caseKey="" own={sess.info.own} nb={sess.info.nb} size={40} />
            : <CaseDiagram layer={terrainLayer} caseKey={sess.key} own={sess.info.own} nb={sess.info.nb} size={40} />}
          <div className="te-title">
            <b>{sess.created ? (sess.req.mode === 'copy' ? 'Edit a copy' : 'Draw a new tile') : 'Edit tile'}</b>
            <span className="muted small">
              {tsName} · {sess.req.layer === 'dirt' ? 'dirt' : sess.req.layer === 'grass' ? 'grass' : sess.req.corner ? CORNER_LABEL[sess.req.corner] : 'spike'} · cell {sess.req.x}, {sess.req.y}
              {sess.req.scope === 'general' && cs.general ? ' · any corner shape' : sess.req.scope === 'light' ? ' · light and dark neighbours' : sess.req.scope === 'neighbours' ? ' · only these exact neighbours' : ''}
            </span>
          </div>
          <span className="te-grow" />
          {status && <span className="te-status">{status}</span>}
          <button className="btn" onClick={pickFromLevel} title="Tap a cell in the level to open its tile beside yours"><Icon name="target" size={16} /> Pick from level</button>
          <button className="btn" onClick={cancel}>Cancel</button>
          {sess.created && sess.req.layer !== 'spike' && <button className="btn" onClick={saveAnother} title="Save, then draw another variant for the same case (starts from this tile)">Save & another variant</button>}
          {queue.length > 0 && <button className="btn" onClick={saveNext} title="Save and draw the next missing tile">Save & next ({queue.length})</button>}
          <button className="btn primary" onClick={() => save()}><Icon name="check" size={16} /> Save</button>
        </div>
        <div className="te-body">
          <div className={`toolbar-slot te-toolbar ${settings.teToolbarPinned ? 'pinned' : ''}`}>
            <div className={`toolbar ${settings.teToolbarPinned || tbHover ? 'open' : ''}`} onPointerEnter={(e) => tbHoverTo(true, e)} onPointerLeave={(e) => tbHoverTo(false, e)}>
              {TOOLS.map((t) => (
                <button key={t.id} className={`tool ${tool === t.id ? 'active' : ''}`} title={`${t.label} (${t.key.toUpperCase()})${t.tip ? '. ' + t.tip : ''}`} onClick={() => setTool(t.id)}>
                  <Icon name={t.icon} size={20} />
                  <span className="tool-label">{t.label}</span>
                  <kbd>{t.key.toUpperCase()}</kbd>
                </button>
              ))}
              <span className="te-sep" />
              <button className="tool" title="Undo (Ctrl+Z)" disabled={!undo.length && !floating} onClick={doUndo}><Icon name="undo" size={20} /><span className="tool-label">Undo</span></button>
              <button className="tool" title="Redo (Ctrl+Shift+Z)" disabled={!redo.length} onClick={doRedo}><Icon name="redo" size={20} /><span className="tool-label">Redo</span></button>
              <span className="toolbar-gap" />
              <button
                className="tool toolbar-pin"
                title={settings.teToolbarPinned ? 'Collapse the tool bar (it still expands on hover)' : 'Keep the tool bar expanded'}
                onClick={() => {
                  clearTimeout(tbTimer.current);
                  setTbHover(false);
                  settings.update({ teToolbarPinned: !settings.teToolbarPinned });
                }}
              >
                <Icon name={settings.teToolbarPinned ? 'panelclose' : 'panelopen'} size={20} />
                <span className="tool-label">{settings.teToolbarPinned ? 'Collapse' : 'Keep expanded'}</span>
              </button>
            </div>
          </div>
          <div className="te-center">
            <div className="te-options">
              {(tool === 'pencil' || tool === 'eraser' || tool === 'line' || tool === 'rect' || tool === 'fill') && (
                <>
                  <Chip on={opts.mirrorX} onClick={() => setOpt({ mirrorX: !opts.mirrorX })} title="Mirror left / right while drawing"><Icon name="mirror" size={14} /> X</Chip>
                  <Chip on={opts.mirrorY} onClick={() => setOpt({ mirrorY: !opts.mirrorY })} title="Mirror top / bottom while drawing"><Icon name="mirror" size={14} /> Y</Chip>
                </>
              )}
              {tool === 'pencil' && <Chip on={opts.pixelPerfect} onClick={() => setOpt({ pixelPerfect: !opts.pixelPerfect })} title="Freehand lines without doubled corner pixels">Pixel perfect</Chip>}
              {tool === 'fill' && <Chip on={opts.contiguous} onClick={() => setOpt({ contiguous: !opts.contiguous })} title="Off: replace that colour everywhere in the tile">Connected only</Chip>}
              {tool === 'rect' && <Chip on={opts.filled} onClick={() => setOpt({ filled: !opts.filled })}>Filled</Chip>}
              {(tool === 'fill' || tool === 'rect') && <Chip on={opts.dither} onClick={() => setOpt({ dither: !opts.dither })} title="Checker pattern of the main and second colour">Dither</Chip>}
              {(tool === 'select' || tool === 'lasso' || floating || mask) && (
                <>
                  <button className="chip" onClick={() => copy()} title="Copy (Ctrl+C): the selection, or the whole tile"><Icon name="copy" size={14} /> Copy</button>
                  <button className="chip" onClick={() => copy(true)} title="Cut (Ctrl+X)"><Icon name="cut" size={14} /> Cut</button>
                  <button className="chip" disabled={!clipboard} onClick={() => clipboard && pasteImg(clipboard)} title="Paste (Ctrl+V) as a floating piece: drag it, then Apply"><Icon name="paste" size={14} /> Paste</button>
                  {(floating || mask) && <button className="chip" onClick={del} title="Delete"><Icon name="trash" size={14} /></button>}
                  {floating && <button className="chip active" onClick={commitFloating} title="Apply the floating piece (Enter)"><Icon name="check" size={14} /> Apply</button>}
                  {(floating || mask) && <button className="chip" onClick={deselect} title="Deselect (Ctrl+D; or tap without dragging with the select tool). A floating piece is applied first."><Icon name="close" size={14} /> Deselect</button>}
                </>
              )}
              <span className="te-grow" />
              <button className="chip" title={floating || mask ? 'Flip the selection left / right' : 'Flip the tile left / right'} onClick={() => transform(flipH)}><Icon name="flipx" size={14} /></button>
              <button className="chip" title={floating || mask ? 'Flip the selection up / down' : 'Flip the tile up / down'} onClick={() => transform(flipV)}><Icon name="flipy" size={14} /></button>
              <button className="chip" title="Rotate a quarter turn clockwise" onClick={() => transform((a) => (a.w === a.h ? rotate(a, true) : a))}><Icon name="rotate" size={14} /></button>
              <button className="chip" title="Shift the tile 1 pixel left (wraps around; arrow keys)" onClick={() => nudge(-1, 0)}>←</button>
              <button className="chip" title="Shift right" onClick={() => nudge(1, 0)}>→</button>
              <button className="chip" title="Shift up" onClick={() => nudge(0, -1)}>↑</button>
              <button className="chip" title="Shift down" onClick={() => nudge(0, 1)}>↓</button>
              <button className="chip" title="Clear the tile" onClick={() => put(cloneImg({ ...img, d: new Uint8ClampedArray(img.d.length) }))}>Clear</button>
            </div>
            <div className="te-stage">
            <div className="te-canvas-wrap" ref={wrapRef}>
              <canvas
                ref={canvasRef}
                className="te-canvas"
                style={{ width: size[0], height: size[1], cursor: tool === 'picker' ? 'crosshair' : 'default' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={() => setHover(null)}
                onWheel={onWheel}
              />
              <div className="te-zoom">
                <button className="chip" onClick={() => setView({ ...view, z: Math.max(2, z - Math.max(1, Math.round(z / 6))) })}>−</button>
                <button className="chip" onClick={() => setView({ z: 0, px: 0, py: 0 })} title="Fit">{z}×</button>
                <button className="chip" onClick={() => setView({ ...view, z: Math.min(80, z + Math.max(1, Math.round(z / 6))) })}>+</button>
              </div>
              <canvas ref={previewRef} className="te-preview" width={9 * N + 6} height={6 * N} title="Actual size and 2×" />
            </div>
            <div className="te-refs-wrap">
            <PaneResizer width={refsW} min={110} max={Math.max(200, Math.round(window.innerWidth * 0.45))} initial={170} onResize={setRefsW} onDone={(w) => { setRefsW(w); settings.update({ teRefsWidth: w }); }} />
            <RefColumn
              refs={refs}
              width={refsW}
              layout={refLayout}
              onLayout={setRefLayout}
              onClose={(r) => setRefs(refs.filter((q) => !sameRef(q, r)))}
              onFlip={(r, flags) => setRefs(refs.map((q) => (sameRef(q, r) ? { ...q, flags } : q)))}
              onPiece={pastePiece}
              onColor={pickColor}
              onStart={(p) => { setFloating(null); setMask(null); put(p); }}
              onAddFromLevel={pickFromLevel}
              onHover={setRefHover}
              hover={refHover ?? hover}
              brush={tool === 'pencil'}
              onBrush={brushFromRef}
            />
            </div>
            </div>
            <div className="te-show">
              <Chip on={show.neighbours} onClick={() => setShowOpt({ neighbours: !show.neighbours })} title="The 8 cells around this one, as in the level">Neighbours</Chip>
              {sess.req.layer !== 'spike' && <Chip on={show.other} onClick={() => setShowOpt({ other: !show.other })} title={sess.req.layer === 'grass' ? 'The dirt under the grass' : 'The grass over the dirt'}>{sess.req.layer === 'grass' ? 'Dirt below' : 'Grass above'}</Chip>}
              <Chip on={show.guide} onClick={() => setShowOpt({ guide: !show.guide })} title={sess.req.layer === 'spike' ? 'Orange lines: the two spike sides of this inside corner' : 'Where the ground surface runs: blue / green lines face the air, dots are open inside corners'}>{sess.req.layer === 'spike' ? 'Corner guide' : 'Terrain guide'}</Chip>
              <Chip on={show.collision} onClick={() => setShowOpt({ collision: !show.collision })} title="Red: outside the cell's solid shape (no collision there)">Solid area</Chip>
              <Chip on={show.dim} onClick={() => setShowOpt({ dim: !show.dim })} title="Darken the neighbours (off: true colours, to check the seams)">Dim neighbours</Chip>
              <Chip on={show.grid} onClick={() => setShowOpt({ grid: !show.grid })}>Pixel grid</Chip>
              <Chip on={show.seams} onClick={() => setShowOpt({ seams: !show.seams })} title="Red marks: edge pixels whose opacity does not continue into the neighbour tile">Seams</Chip>
              {seamText && <span className="te-warn">{seamText}</span>}
            </div>
          </div>
          <div className="te-side-wrap" style={{ width: sideW }}>
          <PaneResizer width={sideW} min={260} max={Math.max(340, Math.round(window.innerWidth * 0.55))} initial={330} onResize={setSideW} onDone={(w) => { setSideW(w); settings.update({ teSideWidth: w }); }} />
          <div className="te-side">
            <section>
              <div className="te-colors">
                <button className="te-swatch big" style={{ background: cssOf(primary) }} title={`Main colour ${hexOf(primary)} (left button)`} />
                <button className="te-swatch" style={{ background: cssOf(secondary) }} title={`Second colour ${hexOf(secondary)} (right button, dither)`} onClick={() => { setPrimary(secondary); setSecondary(primary); }} />
                <button className="chip" title="Swap main and second colour (X)" onClick={() => { setPrimary(secondary); setSecondary(primary); }}>⇄</button>
                <input type="color" className="te-color-input" disabled={lock} title={lock ? 'Palette lock is on: pick from the palette' : 'Any colour'} value={hexOf(primary).slice(0, 7).replace('transparent', '#000000')} onChange={(e) => setPrimary(parseHex(e.target.value))} />
                <button className="chip" title="Keep the main colour in this tileset's saved colours" onClick={() => {
                  const h = hexOf(primary);
                  if (!primary || saved.includes(h)) return;
                  const n = [...saved, h];
                  setSaved(n);
                  setSheetColors(styleId, n);
                  saveCustomSheet(styleId);
                }}><Icon name="plus" size={14} /> Save</button>
              </div>
              <div className="te-tabs">
                <button className={palTab === 'tileset' ? 'active' : ''} onClick={() => setPalTab('tileset')}>Tileset</button>
                <button className={palTab === 'tile' ? 'active' : ''} onClick={() => setPalTab('tile')} title="Colours of this tile and the source tile">This tile</button>
                <button className={palTab === 'saved' ? 'active' : ''} onClick={() => setPalTab('saved')}>Saved ({saved.length})</button>
              </div>
              <div className="te-palette">
                {paletteShown.map((c) => (
                  <button
                    key={c}
                    className={`te-swatch ${c === primary ? 'on' : ''}`}
                    style={{ background: cssOf(c) }}
                    title={hexOf(c)}
                    onClick={() => setPrimary(c)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (palTab === 'saved') {
                        const n = saved.filter((h) => parseHex(h) !== c);
                        setSaved(n);
                        setSheetColors(styleId, n);
                        saveCustomSheet(styleId);
                      } else setSecondary(c);
                    }}
                  />
                ))}
                {!paletteShown.length && <span className="muted small">{palTab === 'saved' ? 'No saved colours yet: pick one and press Save.' : 'No colours.'}</span>}
              </div>
              <div className="te-row">
                <label className="te-check"><input type="checkbox" checked={lock} onChange={(e) => setLock(e.target.checked)} /> Palette lock</label>
                {lock && offPalette.length > 0 && (
                  <>
                    <span className="te-warn">{offPalette.length} off-palette</span>
                    <button className="chip" onClick={snapToPalette} title="Change off-palette pixels to the nearest tileset or saved colour">Snap to palette</button>
                  </>
                )}
              </div>
              <div className="muted small">Right-click a swatch: second colour{palTab === 'saved' ? ' (saved colours: remove)' : ''}.</div>
            </section>

            {sess.variants.length > 0 && (
              <section>
                <h4>Colour variations</h4>
                <div className="te-variants">
                  {sess.variants.map((name, vi) => {
                    const k = colorKeyOf(sess, vi);
                    const has = !!imgs[k];
                    return (
                      <div key={name} className={`te-variant ${color === k ? 'active' : ''}`}>
                        <button onClick={() => switchColor(k)} title={has ? `Edit the ${name} version` : `Make the ${name} version by palette swap`}>
                          {has ? <ImgThumb img={imgs[k]} size={32} /> : <span className="te-novar">+</span>}
                          <span>{name}{vi === 0 ? ' (main)' : ''}</span>
                        </button>
                        {has && vi > 0 && <button className="te-x" title="Remove this colour version (uses the main colours)" onClick={() => dropColor(k)}>×</button>}
                        {vi > 0 && has && <button className="te-map" title="Edit how colours map to this variation" onClick={() => setSwapEdit(swapEdit === k ? null : k)}>map</button>}
                      </div>
                    );
                  })}
                </div>
                <div className="te-row">
                  <label className="te-check"><input type="checkbox" checked={sync} onChange={(e) => setSync(e.target.checked)} /> Keep colours in sync</label>
                  <button className="chip" onClick={makeAllColors}>Make all colours</button>
                </div>
                {swapEdit && imgs[swapEdit] && (
                  <SwapEditor
                    styleId={styleId}
                    from={color === swapEdit ? '' : color}
                    to={swapEdit}
                    img={imgs[color === swapEdit ? ''  : color] ?? img}
                    onChange={() => {
                      const from = color === swapEdit ? '' : color;
                      const made = applySwap(imgs[from] ?? img, swapMap(styleId, from, swapEdit));
                      const all = { ...imgs, [swapEdit]: made };
                      writeColor(sess, swapEdit, made);
                      setImgs(all);
                    }}
                  />
                )}
                <div className="muted small">Each colour variation of the tileset gets its own version; missing ones are made by swapping colours the way the tileset's own pieces differ.</div>
              </section>
            )}

            {sess.created && sess.req.layer !== 'spike' && (
              <section>
                <h4>Tiles for this case</h4>
                <div className="te-row">
                  <label className="te-check"><input type="radio" checked={linkMode === 'replace'} onChange={() => setLinkMode('replace')} /> {sess.req.mode === 'copy' ? 'Instead of the original' : 'Only this tile'}</label>
                  <label className="te-check"><input type="radio" checked={linkMode === 'add'} onChange={() => setLinkMode('add')} /> Add as another variant</label>
                </div>
                {caseList && (
                  <div className="te-caselist">
                    {pairs(caseList).map(([c, w], i) => (
                      <div key={i} className="te-caseitem" title={`weight ${w}`}>
                        <ChoiceThumb d={d} table={table} choice={c} variant={sess.info.variant} size={32} />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {sess.req.layer === 'dirt' && (
              <section>
                <h4>Collision</h4>
                <div className="te-row">
                  <label className="te-check"><input type="radio" checked={collision === 'shape'} onChange={() => { setCollision('shape'); setTileShape(styleId, sess.tileN, sess.info.own); }} /> Terrain shape</label>
                  <label className="te-check"><input type="radio" checked={collision === 'pixels'} onChange={() => { setCollision('pixels'); setTileShape(styleId, sess.tileN, undefined); }} /> Drawn pixels</label>
                </div>
                <div className="muted small">In play, solid tiles collide with {collision === 'shape' ? "the cell's terrain shape, whatever is drawn" : 'their opaque pixels'}.</div>
              </section>
            )}

            <SourcePane
              styleId={styleId}
              source={source}
              onSource={setSource}
              onPaste={(p) => pasteImg(p)}
              onOpen={openRef}
              onStart={(p) => { setFloating(null); setMask(null); put(p); }}
            />

            {history.length > 0 && (
              <section>
                <h4>History</h4>
                <div className="te-history">
                  {[...history].reverse().map((v, i) => (
                    <button key={i} className="te-hist" title={`Load the version from ${new Date(v.at).toLocaleString()}`} onClick={() => put(applyFlags(tileImg({ ...tile!, px: v.px, variants: v.variants }, color || null), sess.flags))}>
                      <ImgThumb img={tileImg({ ...tile!, px: v.px, variants: v.variants }, color || null)} size={32} />
                      <span className="muted small">{new Date(v.at).toLocaleDateString()}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({ on, onClick, title, children }: { on: boolean; onClick: () => void; title?: string; children: React.ReactNode }) {
  return <button className={`chip ${on ? 'active' : ''}`} onClick={onClick} title={title}>{children}</button>;
}

function firstColor(a: Img): number | null {
  const m = paletteOf([a], 1);
  return m[0] ?? null;
}

export function ImgThumb({ img, size }: { img: Img; size: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    c.width = img.w;
    c.height = img.h;
    c.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(img.d), img.w, img.h), 0, 0);
  }, [img]);
  return <canvas ref={ref} className="te-thumb" style={{ width: size, height: size }} />;
}

function SwapEditor({ styleId, from, to, img, onChange }: { styleId: string; from: string; to: string; img: Img; onChange: () => void }) {
  const [, setT] = useState(0);
  const map = swapMap(styleId, from, to);
  const colors = paletteOf([img], 32);
  const key = `${from}>${to}`;
  const edits = customSheet(styleId)?.swaps?.[key] ?? {};
  const targets = tilesetPalette(styleId, to);
  return (
    <div className="te-swap">
      <div className="muted small">{from || 'main'} → {to}: click a target colour to change it.</div>
      {colors.map((c) => {
        const mapped = map.get(c) ?? (map.size ? map.get(nearestColor(c, [...map.keys()])) : c) ?? c;
        return (
          <div key={c} className="te-swap-row">
            <span className="te-swatch" style={{ background: cssOf(c) }} title={hexOf(c)} />
            <span>→</span>
            <select
              value={hexOf(mapped)}
              onChange={(e) => {
                const n = { ...edits, [hexOf(c)]: e.target.value };
                setSheetSwap(styleId, key, n);
                saveCustomSheet(styleId);
                setT((x) => x + 1);
                onChange();
              }}
            >
              {[mapped, ...targets.filter((x) => x !== mapped)].map((x) => (
                <option key={x} value={hexOf(x)} style={{ background: cssOf(x) }}>{hexOf(x)}</option>
              ))}
            </select>
            <span className="te-swatch" style={{ background: cssOf(mapped) }} />
            {edits[hexOf(c)] && (
              <button className="te-x inline" title="Back to the learned colour" onClick={() => {
                const n = { ...edits };
                delete n[hexOf(c)];
                setSheetSwap(styleId, key, n);
                saveCustomSheet(styleId);
                setT((x) => x + 1);
                onChange();
              }}>×</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function sourceImg(s: SourcePick): Img | null {
  const p = spritePixels(s.pe);
  return p ? applyFlags(p, s.flags) : null;
}
